import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { authApi, formatApiError } from "../lib/api";
import { CheckCircle2 } from "lucide-react";

export default function ActivatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const digitsOnly = (s) => (s || "").replace(/\D/g, "").slice(0, 4);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (p1.length !== 4 || !/^\d{4}$/.test(p1)) {
      setError("PIN-koden måste vara exakt fyra siffror.");
      return;
    }
    if (p1 !== p2) {
      setError("PIN-koderna matchar inte.");
      return;
    }
    setBusy(true);
    try {
      await authApi.activate(token, p1);
      setOk(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col"
         style={{ background: "linear-gradient(180deg,#F0F6FC 0%,#ffffff 100%)" }}>
      <header className="px-6 sm:px-10 py-6 flex items-center">
        <img src="/valfrimobil-logo.webp" alt="ValfriMobil" className="h-10 w-auto" />
      </header>
      <main className="flex-1 flex items-start justify-center px-4 pt-10 sm:pt-16">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-slate-100 p-8 sm:p-10 fade-in-up">
          {ok ? (
            <div className="text-center py-6" data-testid="activation-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h1 className="text-2xl font-bold display-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
                PIN-kod sparad
              </h1>
              <p className="text-slate-500 mt-2">Du skickas vidare till inloggningen…</p>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold display-tight mb-1.5"
                  style={{ fontFamily: "Manrope, sans-serif", color: "#0F172A" }}>
                Aktivera <span style={{ color: "#3B9EDA" }}>konto</span>
              </h1>
              <p className="text-slate-500 text-sm mb-7">
                Välj en PIN-kod på exakt fyra siffror (0001–9999). Länken är engångsgiltig och gäller 30 minuter.
              </p>
              <form onSubmit={submit} className="space-y-4" autoComplete="off">
                <div className="space-y-1.5">
                  <Label htmlFor="p1">Ny PIN-kod</Label>
                  <Input id="p1" type="password"
                         inputMode="numeric"
                         pattern="[0-9]{4}"
                         maxLength={4}
                         autoComplete="new-password"
                         data-testid="input-new-password"
                         value={p1}
                         onChange={(e) => setP1(digitsOnly(e.target.value))}
                         placeholder="4 siffror" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="p2">Bekräfta PIN-kod</Label>
                  <Input id="p2" type="password"
                         inputMode="numeric"
                         pattern="[0-9]{4}"
                         maxLength={4}
                         autoComplete="new-password"
                         data-testid="input-confirm-password"
                         value={p2}
                         onChange={(e) => setP2(digitsOnly(e.target.value))}
                         placeholder="Skriv samma PIN-kod igen" required />
                </div>
                {error && (
                  <div data-testid="activation-error"
                       className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200">
                    {error}
                  </div>
                )}
                <Button type="submit" disabled={busy}
                        data-testid="button-activate"
                        className="w-full rounded-full py-6 text-white text-base font-bold"
                        style={{ backgroundColor: "#3B9EDA" }}>
                  {busy ? "Sparar…" : "Spara PIN-kod"}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
